import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { StatusCheckPredicateConfiguration } from 'components/status-checks/form/status-check/StatusCheckPredicateConfiguration'
import { TestStatusCheckSuccessEvaluation } from 'components/status-checks/form/status-check/TestStatusCheckSuccessEvaluation'
import {
  type StatusCheckFormValues,
  buildEvaluationConfigurationFromFormValues,
  MIN_OK_LATENCY_MS
} from 'components/status-checks/status-checks-form-utils'
import { getIsStandardEndpointResponse } from 'components/status-checks/status-checks-form-validation-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'

export const StatusCheckSuccessEvaluation: React.FC = () => {
  const {
    endpointResponse,
    evaluationResults,
    isTestSuccessEvaluationDisabled,
    shouldDisplayPredicateConfiguration,
    setEvaluationResults
  } = useStatusCheckDetailsContext()

  const { control, formState, getValues, resetField, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [endpointConfiguration, isCustomRequestTimeout] = useWatch({
    control,
    name: [
      'endpointConfiguration',
      'evaluationConfiguration.isCustomRequestTimeout'
    ]
  })

  // Set the predicates field as an empty array if the predicate configuration is not displayed.
  useEffect(() => {
    if (shouldDisplayPredicateConfiguration) return

    setValue('evaluationConfiguration.responseBodyEvaluation.predicates', [])
  }, [shouldDisplayPredicateConfiguration, setValue])

  useEffect(() => {
    if (isCustomRequestTimeout) {
      trigger('evaluationConfiguration.requestTimeout')
    } else {
      resetField('evaluationConfiguration.requestTimeout')
    }
  }, [isCustomRequestTimeout, trigger, resetField])

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h6">Success Evaluation</Typography>
        <Typography>
          This criteria will tell us if the target or service is weathering the
          tests.
        </Typography>
      </Stack>
      <FormControl>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="healthyStatusCode"
            render={({ field }) => {
              const { healthyStatusCode } = formState.errors || {}
              const isDisabled = Boolean(!endpointResponse)
              const hasError = Boolean(healthyStatusCode)
              const helperText =
                healthyStatusCode?.message ??
                'Use a single status code (200) or a range (200-299)'

              return (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  error={hasError}
                  helperText={helperText}
                  label="Healthy Status Code"
                  sx={{ width: '50%' }}
                  variant="outlined"
                  onChange={(e) => {
                    const formValues = {
                      ...getValues(),
                      healthyStatusCode: e.target.value
                    }

                    setValue(
                      'evaluationConfiguration',
                      buildEvaluationConfigurationFromFormValues(formValues)
                    )

                    field.onChange(e)
                  }}
                />
              )
            }}
          />
          <Controller
            control={control}
            name="evaluationConfiguration.isCustomRequestTimeout"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={Boolean(!endpointResponse)}
                  />
                }
                label={
                  <>
                    <Typography variant="body1">
                      Use custom request timeout
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      This setting will make this health check fail if it does
                      not respond within a certain time.
                    </Typography>
                  </>
                }
              />
            )}
          />
          {isCustomRequestTimeout && (
            <Controller
              control={control}
              name="evaluationConfiguration.requestTimeout"
              render={({ field }) => {
                const { evaluationConfiguration } = formState.errors || {}

                const isDisabled = Boolean(!endpointResponse)
                const hasError = Boolean(
                  evaluationConfiguration?.requestTimeout
                )
                const helperText =
                  evaluationConfiguration?.requestTimeout?.message ?? ''

                return (
                  <TextField
                    {...field}
                    disabled={isDisabled}
                    error={hasError}
                    helperText={helperText}
                    inputProps={{
                      min: MIN_OK_LATENCY_MS
                    }}
                    label="Request Timeout (ms)"
                    type="number"
                    value={field.value === null ? '' : field.value}
                    variant="outlined"
                    onChange={(e) => {
                      const value = e.target.value
                      // Necessary because this value is converted to a string at the DOM level
                      const newRequestTimeout =
                        value === '' ? null : parseInt(value)
                      const formValues = {
                        ...getValues(),
                        requestTimeout: newRequestTimeout
                      }

                      setValue(
                        'evaluationConfiguration',
                        buildEvaluationConfigurationFromFormValues(formValues)
                      )

                      field.onChange(newRequestTimeout)
                    }}
                    onKeyDown={(e) => {
                      // these keys are allowed by default in html number input type
                      const blockedKeys = new Set(['-', '+', 'e', '.'])
                      if (blockedKeys.has(e.key)) {
                        e.preventDefault()
                      }
                    }}
                  />
                )
              }}
            />
          )}
          {shouldDisplayPredicateConfiguration && (
            <StatusCheckPredicateConfiguration />
          )}
          {getIsStandardEndpointResponse(endpointResponse) && (
            <TestStatusCheckSuccessEvaluation
              disabled={isTestSuccessEvaluationDisabled}
              endpointConfiguration={endpointConfiguration}
              endpointResponse={endpointResponse}
              evaluationResults={evaluationResults}
              onChange={setEvaluationResults}
            />
          )}
        </Stack>
      </FormControl>
    </Stack>
  )
}
